import React, { useState, useEffect } from 'react';
import { Box, Typography, Paper, Grid, Card, CardContent, CircularProgress, IconButton, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { getFinance } from '../api';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as ReTooltip, CartesianGrid, Legend } from 'recharts';

// 💎 PlayLanguage (Phase F): colors for the per-product stacked revenue chart.
const PRODUCT_COLORS = { media: 'var(--ac1, #7c4dff)', playlanguage: '#5eead4' };

export default function FinancePage() {
  const tok = localStorage.getItem('dashboard_token');
  const resetStat = async (key) => {
    if (!window.confirm('Скинути до 0?')) return;
    await fetch('/api/stats/reset?key='+key, { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    window.location.reload();
  };
  const restoreStat = async (key) => {
    await fetch('/api/stats/restore?key='+key, { method:'POST', headers:{ Authorization:'Bearer '+tok } });
    window.location.reload();
  };
  const [data, setData] = useState(null);          // /api/finance (pure Media)
  const [products, setProducts] = useState(null);  // 💎 Phase F: /api/finance/products (Media + PL)
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      getFinance().then(r => r.data).catch(() => null),
      fetch('/api/finance/products', { headers: { Authorization: 'Bearer ' + tok } })
        .then(r => (r.ok ? r.json() : null)).catch(() => null),
    ]).then(([fin, prod]) => { setData(fin); setProducts(prod); setLoading(false); });
  }, []);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 10 }}><CircularProgress sx={{ color: 'var(--ac1, #7c4dff)' }}/></Box>;

  // 💎 Phase F: combined monthly (Media + PlayLanguage), oldest→newest for the chart.
  // Falls back to the pure-Media monthly if the products endpoint is unavailable.
  const chartData = (products?.monthly || data?.monthly || []).map(m => ({
    month: m.month,
    media: parseFloat(m.media != null ? m.media : (m.total || 0)),
    playlanguage: parseFloat(m.playlanguage || 0),
  })).reverse();
  const grandTotal = products ? products.grand_total : (data?.total || 0);
  const productCards = products?.products || (data ? [{ key: 'media', label: 'Media', total: data.total || 0, logo: '/icon-192.png' }] : []);

  return (
    <Box>
      <Typography variant="h5" sx={{ color: '#fff', mb: 3, fontWeight: 'bold' }}>💰 Фінанси</Typography>
      <Grid container spacing={2} sx={{ mb: 3 }}>
        {/* 💎 Phase F: grand total (Media + PlayLanguage) */}
        <Grid item xs={12} md={4}>
          <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid #333' }}>
            <CardContent>
              <Typography variant="body2" sx={{ color: '#aaa' }}>💰 Разом (Media + PlayLanguage)</Typography>
              <Typography variant="h4" sx={{ color: '#4caf50', fontWeight: 'bold', mt: 1 }}>
                {Number(grandTotal || 0).toFixed(0)} EUR
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        {/* 💎 Phase F: per-product breakdown with real logos */}
        {productCards.map(p => (
          <Grid item xs={12} md={4} key={p.key}>
            <Card sx={{ bgcolor: '#1a1a2e', border: '1px solid #333' }}>
              <CardContent>
                <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                  <Box sx={{ display:'flex', alignItems:'center', gap:1 }}>
                    <Box component="img" src={p.logo} alt={p.label}
                      sx={{ width:26, height:26, borderRadius:'6px', objectFit:'cover',
                            border:'1px solid ' + (PRODUCT_COLORS[p.key] || '#555') }}/>
                    <Typography variant="body2" sx={{ color: '#aaa' }}>{p.label}</Typography>
                  </Box>
                  {p.key === 'media' && (
                    <Box>
                      <Tooltip title="Скинути Media-дохід до 0"><IconButton size="small" onClick={()=>resetStat('revenue')} sx={{ color:'#555','&:hover':{color:'#f44'}, p:0.3 }}><RefreshIcon sx={{ fontSize:14 }}/></IconButton></Tooltip>
                      <Tooltip title="Відновити"><IconButton size="small" onClick={()=>restoreStat('revenue')} sx={{ color:'#555','&:hover':{color:'#4caf50'}, p:0.3 }}>↩️</IconButton></Tooltip>
                    </Box>
                  )}
                </Box>
                <Typography variant="h4" sx={{ color: PRODUCT_COLORS[p.key] || '#4caf50', fontWeight: 'bold', mt: 1 }}>
                  {Number(p.total || 0).toFixed(0)} EUR
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
      <Paper sx={{ bgcolor: '#1a1a2e', border: '1px solid #333', p: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'var(--ac1, #7c4dff)', mb: 2 }}>📅 Дохід по місяцях (Media + 💎 PlayLanguage)</Typography>
        <ResponsiveContainer width="100%" height={300}>
          <BarChart data={chartData}>
            <CartesianGrid strokeDasharray="3 3" stroke="#333"/>
            <XAxis dataKey="month" stroke="#aaa" fontSize={12}/>
            <YAxis stroke="#aaa" fontSize={12}/>
            <ReTooltip contentStyle={{ background: '#1a1a2e', border: '1px solid #333', color: '#fff' }}/>
            <Legend />
            <Bar dataKey="media" stackId="rev" fill={PRODUCT_COLORS.media} name="Media" radius={[0,0,0,0]}/>
            <Bar dataKey="playlanguage" stackId="rev" fill={PRODUCT_COLORS.playlanguage} name="PlayLanguage" radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </Paper>
    </Box>
  );
}
